import { getBusinessWeekStartDayIndex } from "../../../utilities/businessWeek";
import { SANDBOX_CHANGE_COUNT_TOOLTIP } from "./laborModelTooltipCopy";
import React from "react";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import LoadSpinner from "../../../utilities/LoadSpinner";
import LaborModelInnerDisplay from "./LaborModelInnerDisplay";
import LaborDriversDisplay from "./LaborDriversDisplay";
import LaborModelGuidedSetup from "./LaborModelGuidedSetup";
import LaborModelPlanSummary, {
  getLaborModelImpactDeltas,
  getPublishedHeaderMetrics,
  getSandboxHeaderMetrics,
  LaborSummaryBadge,
} from "./LaborModelPlanSummary";
import PublishLaborModelWarningModal from "./PublishLaborModelWarningModal";
import LaborModelPublicationHistoryModal from "./LaborModelPublicationHistoryModal";
import LaborModelScenarioCompareModal from "./LaborModelScenarioCompareModal";
import LaborModelScenarioHeader from "./LaborModelScenarioHeader";
import LaborModelSection from "./LaborModelSection";
import LaborModelStoreAdjustmentsTable from "./LaborModelStoreAdjustmentsTable";
import LaborModelTestModal from "./LaborModelTestModal";
import LaborStandardsOptimizationDrawer from "./LaborStandardsOptimizationDrawer";
import { getLaborModelEvaluationKey } from "./laborModelAssistantEvaluation";
import {
  buildLaborModelCompanyApiParams,
  buildLaborModelInitialApiCalls,
} from "./laborModelInitialLoad";
import {
  getPublicationDisplayName,
  getPublicationStatus,
  getScenarioDisplayDescription,
  getScenarioDisplayName,
} from "./laborModelPublicationStatus";
import {
  deleteScenarioDriver,
  deleteScenarioProgramFlag,
  deleteScenarioStandard,
  deleteScenarioTimeWindow,
  scenarioSnapshotToEditorData,
  setScenarioAttributeStoreValue,
  setScenarioLocationAdjustment,
  setScenarioProgramFlagStoreValue,
  upsertScenarioAttributeDriver,
  upsertScenarioProgramFlag,
  upsertScenarioRoutedDriver,
  upsertScenarioSalesDriver,
  upsertScenarioStandard,
  upsertScenarioTimeWindow,
} from "./laborModelScenarioSnapshot";
import { getRightworkAPI } from "../requests/utilities/requests";
import { postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_labor_standards,
  API_archive_labor_model_scenario,
  API_compare_labor_model_scenario,
  API_create_labor_model_scenario,
  API_get_labor_model_publication,
  API_get_labor_model_scenario,
  API_get_labor_model_scenarios,
  API_replace_labor_model_scenario_snapshot,
  API_update_labor_model_scenario,
  API_post_labor_standard,
  API_delete_labor_standard,
  API_get_labor_drivers,
  API_post_labor_driver_company_attribute,
  API_post_labor_driver_sales,
  API_post_labor_driver_routed_sales,
  API_post_backfill_labor_driver_sales,
  API_post_backfill_labor_driver_routed_sales,
  API_delete_labor_driver,
  API_get_company_time_windows,
  API_edit_company_time_window,
  API_delete_company_time_windows,
  API_get_departments,
  API_get_stores_company_admin,
  API_evaluate_labor_model_scenario,
  API_get_labor_driver_store_attributes,
  API_post_labor_driver_store_attribute,
  API_get_role_names2,
  API_get_labor_program_flags,
  API_post_labor_program_flag_company,
  API_delete_labor_program_flag_company,
  API_get_labor_program_flags_with_store_overrides,
  API_post_labor_program_flag_store,
  API_publish_labor_standard,
  API_get_labor_model_publications,
  API_get_labor_plan_summary,
  API_revert_labor_standard,
} from "../../../constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TopBarDisplayTitleNoLocationSelect from "../home/top_bar_components/TopBarDisplayTitleNoLocationSelect";
import CompanyNameSidebar from "../home/top_bar_components/CompanyNameSidebar";

const MAX_SCENARIO_NAME_LENGTH = 255;
const EVALUATION_SUMMARY_STORAGE_KEY = "rightwork-labor-model-evaluations";

function getRestoredScenarioName(publication, existingNames) {
  const prefix = "Copy of ";
  let suffix = "";
  let collisionNumber = 2;

  while (true) {
    const availableNameLength =
      MAX_SCENARIO_NAME_LENGTH - prefix.length - suffix.length;
    const publicationName = getPublicationDisplayName(publication).slice(
      0,
      availableNameLength
    );
    const candidate = `${prefix}${publicationName}${suffix}`;
    if (!existingNames.has(candidate)) {
      return candidate;
    }
    suffix = ` (${collisionNumber})`;
    collisionNumber += 1;
  }
}

function formatDateForApi(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getNextBusinessWeekRange(weekStartDay = "monday", weekCount = 1) {
  const dayIndex = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ].indexOf(String(weekStartDay).toLowerCase());
  const startDate = new Date();
  const daysUntilStart = ((dayIndex === -1 ? 1 : dayIndex) - startDate.getDay() + 7) % 7;
  startDate.setDate(startDate.getDate() + daysUntilStart);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + Math.max(1, weekCount) * 7 - 1);
  return {
    dateStart: formatDateForApi(startDate),
    dateEnd: formatDateForApi(endDate),
  };
}

export function getCurrentBusinessWeekRange(
  weekStartDay = "monday",
  weekCount = 1,
  referenceDate = new Date()
) {
  const dayIndex = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ].indexOf(String(weekStartDay).toLowerCase());
  const resolvedDayIndex = dayIndex === -1 ? 1 : dayIndex;
  const startDate = new Date(referenceDate);
  const daysSinceWeekStart =
    (startDate.getDay() - resolvedDayIndex + 7) % 7;
  startDate.setDate(startDate.getDate() - daysSinceWeekStart);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + Math.max(1, weekCount) * 7 - 1);
  return {
    dateStart: formatDateForApi(startDate),
    dateEnd: formatDateForApi(endDate),
  };
}

function getRecentCompletedBusinessWeeksRange(
  weekStartDay = "monday",
  weekCount = 4
) {
  const dayIndex = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ].indexOf(String(weekStartDay).toLowerCase());
  const today = new Date();
  const resolvedDayIndex = dayIndex === -1 ? 1 : dayIndex;
  const daysSinceWeekStart =
    (today.getDay() - resolvedDayIndex + 7) % 7;
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - daysSinceWeekStart);
  const endDate = new Date(currentWeekStart);
  endDate.setDate(currentWeekStart.getDate() - 1);
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - weekCount * 7 + 1);
  return {
    dateStart: formatDateForApi(startDate),
    dateEnd: formatDateForApi(endDate),
  };
}

export function buildLaborPlanSummaryRequestParams(
  companyId,
  historyRange,
  forecastRange = null
) {
  return {
    company_id: companyId,
    start_date: historyRange.dateStart,
    end_date: historyRange.dateEnd,
    include_bottom_up_forecasted: true,
    ...(forecastRange?.dateStart && forecastRange?.dateEnd
      ? {
          forecast_start_date: forecastRange.dateStart,
          forecast_end_date: forecastRange.dateEnd,
        }
      : {}),
  };
}

export function mergeModelPlanEvaluationResults(
  current,
  {
    evaluationKey,
    guidedKey,
    guidedRequestId,
    latestGuidedRequestByKey,
    result,
  }
) {
  const shouldUpdateGuided = Boolean(
    guidedKey && latestGuidedRequestByKey?.[guidedKey] === guidedRequestId
  );
  return {
    ...current,
    [evaluationKey]: result,
    ...(shouldUpdateGuided ? { [guidedKey]: result } : {}),
  };
}

function summarizeEvaluationResults(testResults, contributions = {}) {
  const totals = {
    newHours: 0,
    oldHours: 0,
    newCost: 0,
    oldCost: 0,
    comparableRows: 0,
  };
  const storeImpacts = Object.entries(testResults || {}).map(
    ([storeName, days]) => {
      let hoursDelta = 0;
      let costDelta = 0;
      (days || []).forEach((day) => {
        (day.labor_results || []).forEach((result) => {
          const newHours = Number(result.new_hours);
          const oldHours = Number(result.old_hours);
          const newCost = Number(result.new_cost);
          const oldCost = Number(result.old_cost);
          if (Number.isFinite(newHours)) totals.newHours += newHours;
          if (Number.isFinite(oldHours)) totals.oldHours += oldHours;
          if (Number.isFinite(newCost)) totals.newCost += newCost;
          if (Number.isFinite(oldCost)) totals.oldCost += oldCost;
          if (Number.isFinite(newHours) || Number.isFinite(oldHours)) {
            hoursDelta += (Number.isFinite(newHours) ? newHours : 0) -
              (Number.isFinite(oldHours) ? oldHours : 0);
          }
          if (Number.isFinite(newCost) || Number.isFinite(oldCost)) {
            costDelta += (Number.isFinite(newCost) ? newCost : 0) -
              (Number.isFinite(oldCost) ? oldCost : 0);
          }
          if (
            Number.isFinite(newHours) ||
            Number.isFinite(oldHours) ||
            Number.isFinite(newCost) ||
            Number.isFinite(oldCost)
          ) {
            totals.comparableRows += 1;
          }
        });
      });
      return { storeName, hoursDelta, costDelta };
    }
  );

  return {
    ...totals,
    hasComparableTotals: totals.comparableRows > 0,
    topStoreImpacts: storeImpacts
      .sort((left, right) => Math.abs(right.hoursDelta) - Math.abs(left.hoursDelta))
      .slice(0, 3),
    topContributors: Object.values(contributions || {})
      .sort((left, right) => Math.abs(Number(right.hours || 0)) - Math.abs(Number(left.hours || 0)))
      .slice(0, 3),
  };
}

export const ZERO_CHANGE_IMPACT_DISCLAIMER =
  "No labor-model settings have changed. Impact may still differ because this preview recalculates using current operating data, while published labor reflects the data and model version used when it was last calculated. Differences can come from changes to wages, hours of operation, or date-effective model versions.";

export function isMeaningfullyDisplayedImpact(value) {
  return Number.isFinite(value) && Math.abs(value) >= 0.05;
}

export function shouldShowZeroChangeImpactDisclaimer({
  readOnly,
  changeCount,
  evaluationFresh,
  evaluationLoading,
  evaluationError,
  evaluationDeltas,
}) {
  return (
    !readOnly &&
    changeCount !== null &&
    changeCount !== undefined &&
    Number(changeCount) === 0 &&
    evaluationFresh &&
    !evaluationLoading &&
    !evaluationError &&
    [evaluationDeltas?.earned, evaluationDeltas?.forecasted].some(
      isMeaningfullyDisplayedImpact
    )
  );
}

function ImpactDisclaimerControl({ ariaLabel, copy }) {
  const [open, setOpen] = React.useState(false);

  return (
    <Tooltip
      arrow
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement="bottom-start"
      title={copy}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 400,
            fontSize: "12px",
            lineHeight: 1.5,
            padding: "12px 16px",
          },
        },
      }}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-flex rounded-full text-slate-500 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
      >
        <InfoOutlinedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
      </button>
    </Tooltip>
  );
}

export function LaborModelWorkspaceHeader({
  modelName,
  modelDescription,
  readOnly,
  connected = false,
  summary,
  evaluationFresh,
  evaluationLoading,
  evaluationError,
  evaluationDeltas,
  publishedMetrics,
  sandboxMetrics,
  changeCount,
  changeCountLoading,
  onOpenAssistant,
  onCreateSandbox,
  onViewChanges,
}) {
  const [creatingSandbox, setCreatingSandbox] = React.useState(false);
  const showZeroChangeImpactDisclaimer = shouldShowZeroChangeImpactDisclaimer({
    readOnly,
    changeCount,
    evaluationFresh,
    evaluationLoading,
    evaluationError,
    evaluationDeltas,
  });
  const createSandbox = async () => {
    if (!onCreateSandbox || creatingSandbox) return;
    setCreatingSandbox(true);
    try {
      await onCreateSandbox();
    } catch {
      // The restore flow reports the request error and keeps this model open.
    } finally {
      setCreatingSandbox(false);
    }
  };

  return (
    <section
      className={`bg-white px-5 py-4 ${
        connected
          ? "border-b border-slate-200"
          : "rounded-xl border border-slate-200 shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-6 lg:flex-col lg:items-stretch lg:gap-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-2xl font-semibold tracking-tight text-slate-950">
              {modelName}
            </h2>
            {readOnly ? (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                Published
              </span>
            ) : (
              <span
                role="img"
                aria-label="Sandbox scenario"
                title="Sandbox scenario"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700"
              >
                <ScienceOutlinedIcon aria-hidden="true" sx={{ fontSize: 17 }} />
              </span>
            )}
          </div>
          {modelDescription ? (
            <p className="max-w-3xl text-sm text-slate-500">
              {modelDescription}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            {readOnly && changeCount !== null && changeCount !== undefined ? (
              changeCount > 0 ? (
                <button
                  type="button"
                  onClick={onViewChanges}
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
                >
                  <CompareArrowsIcon aria-hidden="true" sx={{ fontSize: 14 }} />
                  {changeCount} change{changeCount === 1 ? "" : "s"} from live
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">
                  <CompareArrowsIcon aria-hidden="true" sx={{ fontSize: 14 }} />
                  0 changes from live
                </span>
              )
            ) : !readOnly && (changeCountLoading || changeCount !== null) ? (
              <>
                {changeCountLoading ? (
                  <span
                    aria-label="Loading change count"
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500"
                  >
                    <CompareArrowsIcon aria-hidden="true" sx={{ fontSize: 14 }} />
                    … changes
                  </span>
                ) : changeCount > 0 ? (
                  <Tooltip title={SANDBOX_CHANGE_COUNT_TOOLTIP} describeChild enterTouchDelay={0}>
                    <button
                      type="button"
                      onClick={onViewChanges}
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
                    >
                      <CompareArrowsIcon aria-hidden="true" sx={{ fontSize: 14 }} />
                      {changeCount} change{changeCount === 1 ? "" : "s"}
                    </button>
                  </Tooltip>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">
                    <Tooltip title={SANDBOX_CHANGE_COUNT_TOOLTIP} describeChild enterTouchDelay={0}>
                      <span tabIndex={0} className="inline-flex items-center gap-1 cursor-help">
                        <CompareArrowsIcon aria-hidden="true" sx={{ fontSize: 14 }} />
                        0 model changes
                      </span>
                    </Tooltip>
                    {showZeroChangeImpactDisclaimer ? (
                      <ImpactDisclaimerControl
                        ariaLabel="Why this sandbox can show labor impact with no model changes"
                        copy={ZERO_CHANGE_IMPACT_DISCLAIMER}
                      />
                    ) : null}
                  </span>
                )}
              </>
            ) : null}
            {readOnly && publishedMetrics ? (
              <div className="flex w-full flex-wrap gap-2 pt-1">
                {["earned", "forecasted"].map((period) => (
                  <LaborSummaryBadge
                    key={period}
                    scope={summary.stores != null ? `Includes all ${summary.stores} company ${summary.stores === 1 ? "store" : "stores"}. The review below can show a filtered selection.` : "Includes all company stores. The review below can show a filtered selection."}
                    label={period === "earned" ? "Earned" : "Forecasted"}
                    value={publishedMetrics[period]?.splh}
                    metrics={publishedMetrics[period]}
                    laborPercentAvailable={publishedMetrics.laborPercentAvailable}
                  />
                ))}
              </div>
            ) : null}
            {evaluationFresh && !readOnly ? (
              <div className="flex w-full flex-wrap gap-2 pt-1">
                {["earned", "forecasted"].map((period) => (
                  <LaborSummaryBadge
                    key={period}
                    scope={summary.stores != null ? `Includes all ${summary.stores} company ${summary.stores === 1 ? "store" : "stores"}. The review below can show a filtered selection.` : "Includes all company stores. The review below can show a filtered selection."}
                    label={period === "earned" ? "Earned" : "Forecasted"}
                    value={sandboxMetrics?.[period]?.splh}
                    metrics={sandboxMetrics?.[period]}
                    laborPercentAvailable={sandboxMetrics?.laborPercentAvailable}
                    showImpact
                    delta={evaluationDeltas?.[period]}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex w-[390px] shrink-0 flex-col items-stretch gap-3 lg:w-full">
          <dl className="grid grid-cols-3 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
            {[
              [summary.totalDrivers, "Drivers"],
              [summary.standards, "Standards"],
              [summary.adjustments, "Adjustments"],
            ].map(([value, label], index) => (
              <div
                key={label}
                className={`flex min-w-0 items-baseline justify-center gap-1.5 px-3 py-2 ${
                  index ? "border-l border-slate-200" : ""
                }`}
              >
                <dd className="text-sm font-semibold tabular-nums text-slate-900">
                  {value}
                </dd>
                <dt className="truncate text-[11px] font-medium text-slate-500">
                  {label}
                </dt>
              </div>
            ))}
          </dl>

          <div className="flex flex-wrap items-center justify-end gap-2 lg:justify-start">
            <button
              type="button"
              onClick={onOpenAssistant}
              title="Chat about labor model"
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-600 transition-colors hover:border-violet-700 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2"
            >
              <ChatOutlinedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              Chat about labor model
            </button>
            {readOnly && onCreateSandbox ? (
              <button
                type="button"
                disabled={creatingSandbox}
                onClick={createSandbox}
                className="inline-flex h-9 items-center justify-center rounded-lg bg-violet-700 px-4 text-xs font-semibold text-white hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 disabled:cursor-wait disabled:bg-violet-400"
              >
                {creatingSandbox ? "Creating…" : "Create sandbox to edit"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function LaborModelOuterDisplay(props) {
  const [data, setData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [, setIsSavingData] = React.useState(false);
  const [publicationHistoryOpen, setPublicationHistoryOpen] =
    React.useState(false);
  const [scenarioCompareOpen, setScenarioCompareOpen] = React.useState(false);
  const [scenarioChangeCount, setScenarioChangeCount] = React.useState(null);
  const [scenarioChangeCountScenarioUuid, setScenarioChangeCountScenarioUuid] =
    React.useState(null);
  const [scenarioChangeCountLoading, setScenarioChangeCountLoading] =
    React.useState(false);
  const [pendingCompareScenarioUuid, setPendingCompareScenarioUuid] =
    React.useState(null);
  const [scenarioEvaluationOpen, setScenarioEvaluationOpen] =
    React.useState(false);
  const [laborStandardsFitOpen, setLaborStandardsFitOpen] =
    React.useState(false);
  const [evaluationSummariesByScenario, setEvaluationSummariesByScenario] =
    React.useState(() => {
      try {
        return JSON.parse(
          window.sessionStorage.getItem(EVALUATION_SUMMARY_STORAGE_KEY) || "{}"
        );
      } catch (error) {
        return {};
      }
    });
  const [quickEvaluationLoading, setQuickEvaluationLoading] = React.useState(false);
  const [modelPlanEvaluations, setModelPlanEvaluations] = React.useState({});
  const [modelPlanEvaluationLoadingKey, setModelPlanEvaluationLoadingKey] =
    React.useState(null);
  const [modelPlanEvaluationStage, setModelPlanEvaluationStage] =
    React.useState(null);
  const [modelPlanEvaluationErrors, setModelPlanEvaluationErrors] =
    React.useState({});
  const [laborPlanSummary, setLaborPlanSummary] = React.useState(null);
  const [laborPlanSummaryError, setLaborPlanSummaryError] = React.useState("");
  const laborPlanSummaryRequestRef = React.useRef(0);
  const guidedEvaluationRequestRef = React.useRef({});
  const [selectedScenarioUuid, setSelectedScenarioUuid] = React.useState(null);
  const selectedScenarioUuidRef = React.useRef(selectedScenarioUuid);
  selectedScenarioUuidRef.current = selectedScenarioUuid;
  const [selectedScenario, setSelectedScenario] = React.useState(null);
  const [scenarioLoading, setScenarioLoading] = React.useState(false);
  const [modelView, setModelView] = React.useState("saved");
  const [selectedPublicationUuid, setSelectedPublicationUuid] = React.useState(null);
  const [selectedPublication, setSelectedPublication] = React.useState(null);
  const [publicationLoading, setPublicationLoading] = React.useState(false);
  const [assistantOpen, setAssistantOpen] = React.useState(false);
  const defaultDataRequestRef = React.useRef(0);
  const publicationListRequestRef = React.useRef(0);
  const publicationListCompanyRef = React.useRef(null);
  const scenarioListRequestRef = React.useRef(0);
  const scenarioSnapshotRequestRef = React.useRef(0);

  React.useEffect(() => {
    try {
      window.sessionStorage.setItem(
        EVALUATION_SUMMARY_STORAGE_KEY,
        JSON.stringify(evaluationSummariesByScenario)
      );
    } catch (error) {
      // A blocked session store should not prevent model editing or evaluation.
    }
  }, [evaluationSummariesByScenario]);

  const companyOptions = React.useMemo(() => {
    const unique = [];
    props.storeNames?.forEach((s) => {
      if (!unique.some((c) => c.id === s.company)) {
        unique.push({
          id: s.company,
          name: s.company_info?.company_name || s.company_info?.name,
          ...s.company_info,
        });
      }
    });
    return unique;
  }, [props.storeNames]);

  const [selectedCompanyId, setSelectedCompanyId] = React.useState(() => {
    if (props.settingsCompanyId) return props.settingsCompanyId;
    if (props.homeCompany) {
      return props.homeCompany;
    }
    return companyOptions[0]?.id;
  });
  const selectedCompanyIdRef = React.useRef(selectedCompanyId);
  selectedCompanyIdRef.current = selectedCompanyId;

  React.useEffect(() => {
    if (!selectedCompanyId && companyOptions[0]?.id) {
      setSelectedCompanyId(companyOptions[0].id);
    }
  }, [companyOptions, selectedCompanyId]);

  React.useEffect(() => {
    if (!props.homeCompany) {
      return;
    }
    setSelectedCompanyId((current) => {
      if (current === props.homeCompany) {
        return current;
      }
      return props.homeCompany;
    });
  }, [props.homeCompany]);

  const [selectedStoreId, setSelectedStoreId] = React.useState(() => {
    const currentStore = props.storeNames?.find((s) => s.id === props.homeStore);
    if (currentStore?.company === selectedCompanyId) {
      return currentStore.id;
    }
    const firstStore = props.storeNames?.find((s) => s.company === selectedCompanyId);
    return firstStore?.id;
  });

  React.useEffect(() => {
    const matchingStore = props.storeNames?.find((s) => s.id === props.homeStore);
    if (
      matchingStore?.company === selectedCompanyId &&
      props.homeStore !== selectedStoreId
    ) {
      setSelectedStoreId(props.homeStore);
    }
  }, [props.homeStore, props.storeNames, selectedCompanyId, selectedStoreId]);

  const handleCompanyChange = React.useCallback(
    (companyId) => {
      defaultDataRequestRef.current += 1;
      publicationListRequestRef.current += 1;
      scenarioListRequestRef.current += 1;
      scenarioSnapshotRequestRef.current += 1;
      laborPlanSummaryRequestRef.current += 1;
      publicationListCompanyRef.current = null;
      selectedCompanyIdRef.current = companyId;
      setIsLoading(false);
      setScenarioLoading(false);
      setLaborPlanSummary(null);
      setSelectedCompanyId(companyId);
      setSelectedScenarioUuid(null);
      setSelectedScenario(null);
      setModelView("saved");
      setSelectedPublicationUuid(null);
      setSelectedPublication(null);
      setData((current) => ({
        ...current,
        laborModelScenarios: null,
        laborModelPublications: null,
      }));
      const stores = props.storeNames?.filter((s) => s.company === companyId) || [];
      setSelectedStoreId((prevStoreId) => {
        if (prevStoreId && stores.some((s) => s.id === prevStoreId)) {
          return prevStoreId;
        }
        return stores[0]?.id;
      });
    },
    [props.storeNames]
  );

  const companyStores = React.useMemo(
    () =>
      props.storeNames?.filter(
        (store) =>
          store.company === selectedCompanyId &&
          store.status !== "closed" &&
          store.onboarding_status !== "inactive"
      ) || [],
    [props.storeNames, selectedCompanyId]
  );

  React.useEffect(() => {
    if (companyStores.length === 0) {
      setSelectedStoreId(undefined);
      return;
    }
    if (!selectedStoreId || !companyStores.some((s) => s.id === selectedStoreId)) {
      setSelectedStoreId(companyStores[0]?.id);
    }
  }, [companyStores, selectedStoreId]);


  const default_api_calls = React.useMemo(
    () => buildLaborModelInitialApiCalls({
      roleTypes: API_get_departments,
      storesInfo: API_get_stores_company_admin,
      roleNames: API_get_role_names2,
      laborModelScenarios: API_get_labor_model_scenarios,
    }),
    []
  );

  const api_params = React.useMemo(
    () => buildLaborModelCompanyApiParams(selectedCompanyId, companyStores),
    [companyStores, selectedCompanyId]
  );

  const fetch_data = async (
    api_calls,
    api_params,
    showLoading,
    api_params_by_name = {},
    shouldApply = () => true
  ) => {
    if (showLoading) {
      setIsLoading(true);
    }
    let api_set_data_name = Object.keys(api_calls);
    let api_list = Object.values(api_calls);
    try {
      // console.log("CALLING APIS");
      const res = await Promise.all(
        api_list.map((api, index) =>
          getRightworkAPI(
            api,
            api_params_by_name[api_set_data_name[index]] || api_params
          )
        )
      );
      const nextData = {};
      res.forEach((response, i) => {
        nextData[api_set_data_name[i]] = response.data;
      });
      if (!shouldApply()) {
        return;
      }
      setData((prevData) => ({
        ...prevData,
        ...nextData,
      }));
      if (showLoading) {
        setIsLoading(false);
      }
      setIsSavingData(false);
    } catch (error) {
      if (!shouldApply()) {
        return;
      }
      if (showLoading) {
        setIsLoading(false);
      }
      setIsSavingData(false);
      // console.log(error);
      //throw Error(error);
    }
  };

  React.useEffect(() => {
    if (selectedCompanyId && api_params.store_id) {
      const requestId = defaultDataRequestRef.current + 1;
      defaultDataRequestRef.current = requestId;
      fetch_data(
        default_api_calls,
        api_params,
        true,
        {},
        () => defaultDataRequestRef.current === requestId
      );
    }
  }, [
    selectedCompanyId,
    api_params,
    default_api_calls,
  ]);

  const laborModelScenarios = React.useMemo(
    () => data?.laborModelScenarios?.redux?.payload?.scenarios || [],
    [data?.laborModelScenarios]
  );
  const activePublication = React.useMemo(
    () => data?.laborModelScenarios?.redux?.payload?.active_publication || null,
    [data?.laborModelScenarios]
  );
  const laborModelPublications = React.useMemo(
    () => data?.laborModelPublications?.redux?.payload?.publications || [],
    [data?.laborModelPublications]
  );
  const selectedPublicationMetadata = React.useMemo(
    () =>
      laborModelPublications.find(
        (publication) => publication.uuid === selectedPublication?.uuid
      ) || null,
    [laborModelPublications, selectedPublication?.uuid]
  );

  React.useEffect(() => {
    if (!activePublication?.uuid || selectedPublicationUuid) return;
    setSelectedPublicationUuid(activePublication.uuid);
  }, [activePublication, selectedPublicationUuid]);

  React.useEffect(() => {
    const scenariosLoaded = Boolean(data?.laborModelScenarios);
    if (
      !scenariosLoaded ||
      laborModelScenarios.length ||
      !activePublication?.uuid
    ) {
      return;
    }
    setSelectedScenario(null);
    setSelectedScenarioUuid(null);
    setSelectedPublicationUuid(activePublication.uuid);
    setModelView("published");
  }, [
    activePublication,
    data?.laborModelScenarios,
    laborModelScenarios.length,
  ]);

  React.useEffect(() => {
    if (!laborModelScenarios.length) {
      return;
    }
    if (
      selectedScenarioUuid &&
      laborModelScenarios.some(
        (scenario) => scenario.uuid === selectedScenarioUuid
      )
    ) {
      return;
    }
    setSelectedScenarioUuid(laborModelScenarios[0].uuid);
  }, [laborModelScenarios, selectedScenarioUuid]);

  React.useEffect(() => {
    if (!selectedCompanyId || !selectedScenarioUuid) {
      return;
    }
    let isCurrent = true;
    setScenarioLoading(true);
    getRightworkAPI(API_get_labor_model_scenario, {
      company_id: selectedCompanyId,
      scenario_uuid: selectedScenarioUuid,
    })
      .then((response) => {
        if (!isCurrent) return;
        setSelectedScenario(
          response?.data?.redux?.payload?.scenario || null
        );
      })
      .catch((error) => {
        if (!isCurrent) return;
        setSelectedScenario(null);
        toast.error(
          error.response?.data?.system?.message ||
            "Unable to load sandbox scenario",
          { autoClose: 4000 }
        );
      })
      .finally(() => {
        if (isCurrent) setScenarioLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [selectedCompanyId, selectedScenarioUuid]);

  React.useEffect(() => {
    if (
      pendingCompareScenarioUuid &&
      selectedScenario?.uuid === pendingCompareScenarioUuid
    ) {
      setScenarioCompareOpen(true);
      setPendingCompareScenarioUuid(null);
    }
  }, [pendingCompareScenarioUuid, selectedScenario]);

  React.useEffect(() => {
    if (
      modelView !== "published" ||
      !selectedCompanyId ||
      !selectedPublicationUuid
    ) {
      return;
    }
    let isCurrent = true;
    setPublicationLoading(true);
    getRightworkAPI(API_get_labor_model_publication, {
      company_id: selectedCompanyId,
      publication_uuid: selectedPublicationUuid,
    })
      .then((response) => {
        if (!isCurrent) return;
        setSelectedPublication(
          response?.data?.redux?.payload?.publication || null
        );
      })
      .catch((error) => {
        if (!isCurrent) return;
        setSelectedPublication(null);
        toast.error(
          error.response?.data?.system?.message || "Unable to load published model",
          { autoClose: 4000 }
        );
      })
      .finally(() => {
        if (isCurrent) setPublicationLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [modelView, selectedCompanyId, selectedPublicationUuid]);

  const scenarioEditorData = React.useMemo(
    () =>
      selectedScenario?.snapshot
        ? scenarioSnapshotToEditorData(
            selectedScenario.snapshot,
            data.storesInfo?.redux?.payload?.stores || []
          )
        : null,
    [selectedScenario, data.storesInfo]
  );
  const publishedEditorData = React.useMemo(
    () =>
      selectedPublication?.snapshot
        ? scenarioSnapshotToEditorData(
            selectedPublication.snapshot,
            data.storesInfo?.redux?.payload?.stores || []
          )
        : null,
    [selectedPublication, data.storesInfo]
  );
  const modelEditorData =
    modelView === "published" ? publishedEditorData : scenarioEditorData;
  const modelEditorReady = Boolean(modelEditorData);
  const readOnlyModel = modelView === "published";
  const selectedPublishedPlanAvailable = Boolean(
    readOnlyModel &&
      (selectedPublication?.uuid === activePublication?.uuid ||
        getPublicationStatus(
          selectedPublicationMetadata,
          activePublication?.uuid
        ).tone === "scheduled")
  );
  const publishedChangeCount =
    readOnlyModel &&
    selectedPublication?.uuid !== activePublication?.uuid &&
    selectedPublicationMetadata
      ? Number(selectedPublicationMetadata?.change_count_from_live || 0)
      : null;
  React.useEffect(() => {
    if (
      !selectedCompanyId ||
      !modelEditorReady ||
      publicationListCompanyRef.current === selectedCompanyId
    ) {
      return;
    }
    publicationListCompanyRef.current = selectedCompanyId;
    const requestId = publicationListRequestRef.current + 1;
    publicationListRequestRef.current = requestId;
    getRightworkAPI(API_get_labor_model_publications, {
      company_id: selectedCompanyId,
    })
      .then((response) => {
        if (
          publicationListRequestRef.current !== requestId ||
          selectedCompanyIdRef.current !== selectedCompanyId
        ) {
          return;
        }
        setData((current) => ({
          ...current,
          laborModelPublications: response.data,
        }));
      })
      .catch(() => {
        // Publication history is secondary and should not block model editing.
      });
  }, [modelEditorReady, selectedCompanyId]);

  const loadLaborPlanSummary = React.useCallback(
    (
      historyWeekCount = 4,
      forecastRange = null,
      { rejectOnError = false } = {}
    ) => {
      if (!selectedCompanyId || companyStores.length === 0) {
        return Promise.resolve(null);
      }
      const requestId = laborPlanSummaryRequestRef.current + 1;
      laborPlanSummaryRequestRef.current = requestId;
      setLaborPlanSummary(null);
      setLaborPlanSummaryError("");
      const historyRange = getRecentCompletedBusinessWeeksRange(
        companyStores[0]?.business_week_start_day,
        historyWeekCount
      );
      return getRightworkAPI(
        API_get_labor_plan_summary,
        buildLaborPlanSummaryRequestParams(
          selectedCompanyId,
          historyRange,
          forecastRange
        )
      )
        .then((response) => {
          const laborPlan = response?.data?.redux?.payload?.labor_plan || null;
          if (laborPlanSummaryRequestRef.current === requestId) {
            setLaborPlanSummary(laborPlan);
          }
          return laborPlan;
        })
        .catch((error) => {
          if (laborPlanSummaryRequestRef.current === requestId) {
            setLaborPlanSummary(null);
            setLaborPlanSummaryError(
              error.response?.data?.system?.message ||
                "Unable to load the published labor plan."
            );
          }
          if (rejectOnError) {
            throw error;
          }
          return null;
        });
    },
    [companyStores, selectedCompanyId]
  );

  React.useEffect(() => {
    if (
      !modelEditorReady ||
      !readOnlyModel ||
      !selectedPublishedPlanAvailable
    ) {
      return;
    }
    loadLaborPlanSummary(
      4,
      getCurrentBusinessWeekRange(
        companyStores[0]?.business_week_start_day,
        4
      )
    );
  }, [
    activePublication?.uuid,
    companyStores,
    loadLaborPlanSummary,
    modelEditorReady,
    readOnlyModel,
    selectedPublishedPlanAvailable,
  ]);

  const [openPublishWarningModal, setOpenPublishWarningModal] =
    React.useState(false);

  const runQuickEvaluation = React.useCallback(async () => {
    if (!selectedScenario?.uuid || companyStores.length === 0) return;
    const { dateStart, dateEnd } = getNextBusinessWeekRange(
      companyStores[0]?.business_week_start_day
    );
    setQuickEvaluationLoading(true);
    try {
      const response = await postRightworkAPI(API_evaluate_labor_model_scenario, {
        company_id: selectedCompanyId,
        scenario_uuid: selectedScenario.uuid,
        expected_revision: selectedScenario.revision,
        store_ids: companyStores.map((store) => store.id).join(","),
        date_start: dateStart,
        date_end: dateEnd,
        run_type: "forecasted_sandbox_vs_published",
      });
      const evaluationSummary = {
        scenarioUuid: selectedScenario.uuid,
        revision: selectedScenario.revision,
        runType: "forecasted_sandbox_vs_published",
        storeCount: companyStores.length,
        dateStart,
        dateEnd,
        completedAt: new Date().toISOString(),
        ...summarizeEvaluationResults(
          response.data?.test_results,
          response.data?.labor_standard_contribution
        ),
      };
      setEvaluationSummariesByScenario((current) => ({
        ...current,
        [selectedScenario.uuid]: evaluationSummary,
      }));
      toast.success("Labor plan summary updated for the next business week.", {
        autoClose: 3500,
      });
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message || "Unable to evaluate this model.",
        { autoClose: 5000 }
      );
    } finally {
      setQuickEvaluationLoading(false);
    }
  }, [companyStores, selectedCompanyId, selectedScenario]);

  const runModelPlanEvaluation = React.useCallback(
    async ({
      evaluationKey,
      target,
      comparison,
      storeIds,
      rangePreset = "full",
      weeksBack = 4,
      weeksForward = 4,
    }) => {
      if (!evaluationKey || !target?.uuid || !storeIds?.length) return;
      const guidedKey = getLaborModelEvaluationKey(
        target,
        laborModelPublications
      );
      const guidedRequestId = Symbol(evaluationKey);
      if (guidedKey) {
        guidedEvaluationRequestRef.current[guidedKey] = guidedRequestId;
      }
      const historyRange = getRecentCompletedBusinessWeeksRange(
        companyStores[0]?.business_week_start_day,
        weeksBack
      );
      setModelPlanEvaluationLoadingKey(evaluationKey);
      setModelPlanEvaluationErrors((current) => {
        const next = { ...current };
        delete next[evaluationKey];
        return next;
      });
      try {
        const requestBase = {
          company_id: selectedCompanyId,
          target_type: target.type,
          target_uuid: target.uuid,
          target_revision:
            target.type === "scenario" ? target.revision : undefined,
          comparison_type: comparison?.type || "none",
          comparison_uuid: comparison?.uuid || undefined,
          comparison_revision:
            comparison?.type === "scenario"
              ? comparison.revision
              : undefined,
          store_ids: storeIds.join(","),
        };
        setModelPlanEvaluationStage({ evaluationKey, stage: "history" });
        const forecastRange = getCurrentBusinessWeekRange(
          companyStores[0]?.business_week_start_day,
          weeksForward
        );
        const laborPlanSummaryPromise = loadLaborPlanSummary(
          weeksBack,
          forecastRange,
          { rejectOnError: true }
        );
        const [historyResponse, evaluatedLaborPlanSummary] = await Promise.all([
          postRightworkAPI(API_evaluate_labor_model_scenario, {
            ...requestBase,
            date_start: historyRange.dateStart,
            date_end: historyRange.dateEnd,
            run_type: "earned_sandbox_vs_actual",
          }),
          laborPlanSummaryPromise,
        ]);
        setModelPlanEvaluationStage({ evaluationKey, stage: "forecast" });
        const forecastResponse = await postRightworkAPI(
          API_evaluate_labor_model_scenario,
          {
            ...requestBase,
            date_start: forecastRange.dateStart,
            date_end: forecastRange.dateEnd,
            run_type:
              comparison?.type === "published_plan"
                ? "forecasted_sandbox_vs_published"
                : "forecasted_model_vs_model",
          }
        );
        const testResults = forecastResponse.data?.test_results || {};
        const contributions =
          forecastResponse.data?.labor_standard_contribution || {};
        const contributionsByStore =
          forecastResponse.data?.labor_standard_contribution_by_store || {};
        const result = {
          evaluationKey,
          target,
          comparison,
          rangePreset,
          dateStart: forecastRange.dateStart,
          dateEnd: forecastRange.dateEnd,
          completedAt: new Date().toISOString(),
          testResults,
          contributions,
          contributionsByStore,
          laborPlanSummary: evaluatedLaborPlanSummary,
          forecast: {
            dateStart: forecastRange.dateStart,
            dateEnd: forecastRange.dateEnd,
            testResults,
            dailyTestResults:
              forecastResponse.data?.daily_test_results || testResults,
            contributions,
            contributionsByStore,
          },
          history: {
            dateStart: historyRange.dateStart,
            dateEnd: historyRange.dateEnd,
            testResults: historyResponse.data?.test_results || {},
            dailyTestResults:
              historyResponse.data?.daily_test_results ||
              historyResponse.data?.test_results ||
              {},
            contributions:
              historyResponse.data?.labor_standard_contribution || {},
            contributionsByStore:
              historyResponse.data?.labor_standard_contribution_by_store || {},
          },
        };
        setModelPlanEvaluations((current) =>
          mergeModelPlanEvaluationResults(current, {
            evaluationKey,
            guidedKey,
            guidedRequestId,
            latestGuidedRequestByKey: guidedEvaluationRequestRef.current,
            result,
          })
        );

        if (
          target.type === "scenario" &&
          (comparison?.type === "published_plan" ||
            (comparison?.type === "publication" &&
              comparison.uuid === activePublication?.uuid))
        ) {
          setEvaluationSummariesByScenario((current) => ({
            ...current,
            [target.uuid]: {
              scenarioUuid: target.uuid,
              revision: target.revision,
              runType: "forecasted_sandbox_vs_published",
              storeCount: storeIds.length,
              dateStart: forecastRange.dateStart,
              dateEnd: forecastRange.dateEnd,
              completedAt: result.completedAt,
              ...summarizeEvaluationResults(testResults, contributions),
            },
          }));
        }
        return result;
      } catch (error) {
        setModelPlanEvaluationErrors((current) => ({
          ...current,
          [evaluationKey]:
            error.response?.data?.system?.message ||
            "Unable to calculate this labor plan.",
        }));
        return null;
      } finally {
        setModelPlanEvaluationLoadingKey((current) =>
          current === evaluationKey ? null : current
        );
        setModelPlanEvaluationStage((current) =>
          current?.evaluationKey === evaluationKey ? null : current
        );
      }
    },
    [
      activePublication?.uuid,
      companyStores,
      loadLaborPlanSummary,
      laborModelPublications,
      selectedCompanyId,
    ]
  );

  const selectedModelTarget = React.useMemo(() => {
    if (readOnlyModel) {
      return selectedPublication?.uuid
        ? {
            type: "publication",
            uuid: selectedPublication.uuid,
            name: getPublicationDisplayName(selectedPublication),
            version: selectedPublication.version,
          }
        : null;
    }
    return selectedScenario?.uuid
      ? {
          type: "scenario",
          uuid: selectedScenario.uuid,
          name: getScenarioDisplayName(selectedScenario),
          revision: selectedScenario.revision,
        }
      : null;
  }, [readOnlyModel, selectedPublication, selectedScenario]);

  const guidedEvaluationKey = React.useMemo(
    () => getLaborModelEvaluationKey(selectedModelTarget, laborModelPublications),
    [laborModelPublications, selectedModelTarget]
  );
  const guidedEvaluation = guidedEvaluationKey
    ? modelPlanEvaluations[guidedEvaluationKey]
    : null;
  const guidedEvaluationFresh = Boolean(
    selectedModelTarget &&
      guidedEvaluation?.target?.type === selectedModelTarget.type &&
      guidedEvaluation?.target?.uuid === selectedModelTarget.uuid &&
      (selectedModelTarget.type !== "scenario" ||
        Number(guidedEvaluation.target.revision) ===
          Number(selectedModelTarget.revision))
  );
  const updateScenarioState = React.useCallback((scenario) => {
    if (!scenario) return;
    setSelectedScenario((current) =>
      current?.uuid === scenario.uuid
        ? { ...current, ...scenario, snapshot: scenario.snapshot || current.snapshot }
        : scenario
    );
    setSelectedScenarioUuid(scenario.uuid);
    const scenarioSummary = { ...scenario };
    delete scenarioSummary.snapshot;
    setData((current) => {
      const scenarioResponse = current?.laborModelScenarios;
      const payload = scenarioResponse?.redux?.payload;
      if (!payload) return current;
      return {
        ...current,
        laborModelScenarios: {
          ...scenarioResponse,
          redux: {
            ...scenarioResponse.redux,
            payload: {
              ...payload,
              scenarios: (payload.scenarios || []).map((item) =>
                item.uuid === scenario.uuid
                  ? { ...item, ...scenarioSummary }
                  : item
              ),
            },
          },
        },
      };
    });
  }, []);

  const refreshScenarioList = React.useCallback(
    async (preferredUuid = null) => {
      const requestCompanyId = selectedCompanyId;
      const requestId = scenarioListRequestRef.current + 1;
      scenarioListRequestRef.current = requestId;
      const response = await getRightworkAPI(API_get_labor_model_scenarios, {
        company_id: requestCompanyId,
      });
      if (
        scenarioListRequestRef.current !== requestId ||
        selectedCompanyIdRef.current !== requestCompanyId
      ) {
        return [];
      }
      const payload = response?.data?.redux?.payload || {};
      setData((current) => ({
        ...current,
        laborModelScenarios: response.data,
      }));
      const scenarios = payload.scenarios || [];
      const nextUuid =
        preferredUuid && scenarios.some((item) => item.uuid === preferredUuid)
          ? preferredUuid
          : scenarios[0]?.uuid || null;
      setSelectedScenarioUuid(nextUuid);
      return scenarios;
    },
    [selectedCompanyId]
  );

  const saveScenarioSnapshot = React.useCallback(
    async (snapshot, successMessage = "Sandbox scenario saved") => {
      if (!selectedScenario) {
        throw new Error("Select a sandbox scenario before editing.");
      }
      const requestCompanyId = selectedCompanyId;
      const requestScenarioUuid = selectedScenario.uuid;
      const requestId = scenarioSnapshotRequestRef.current + 1;
      scenarioSnapshotRequestRef.current = requestId;
      const shouldApplyResponse = () =>
        scenarioSnapshotRequestRef.current === requestId &&
        selectedCompanyIdRef.current === requestCompanyId &&
        selectedScenarioUuidRef.current === requestScenarioUuid;
      try {
        const response = await postRightworkAPI(
          API_replace_labor_model_scenario_snapshot,
          {
            company_id: requestCompanyId,
            scenario_uuid: requestScenarioUuid,
            expected_revision: selectedScenario.revision,
            snapshot,
          }
        );
        if (!shouldApplyResponse()) {
          return null;
        }
        const scenario = response?.data?.redux?.payload?.scenario;
        updateScenarioState(scenario);
        toast.success(successMessage, { autoClose: 2500 });
        return scenario;
      } catch (error) {
        if (!shouldApplyResponse()) {
          return null;
        }
        const message =
          error.response?.data?.system?.message ||
          error.message ||
          "Unable to save sandbox scenario";
        toast.error(message, { autoClose: 4000 });
        if (message.includes("another session")) {
          setSelectedScenarioUuid(null);
          await refreshScenarioList(selectedScenario.uuid);
        }
        throw error;
      }
    },
    [
      refreshScenarioList,
      selectedCompanyId,
      selectedScenario,
      updateScenarioState,
    ]
  );

  const createScenario = React.useCallback(
    async (values) => {
      try {
        const response = await postRightworkAPI(
          API_create_labor_model_scenario,
          { company_id: selectedCompanyId, ...values }
        );
        const scenario = response?.data?.redux?.payload?.scenario;
        setSelectedScenario(null);
        setModelView("saved");
        await refreshScenarioList(scenario?.uuid);
        toast.success(`Created ${getScenarioDisplayName(scenario)}`, {
          autoClose: 2500,
        });
      } catch (error) {
        toast.error(
          error.response?.data?.system?.message ||
            "Unable to create sandbox scenario",
          { autoClose: 4000 }
        );
        throw error;
      }
    },
    [refreshScenarioList, selectedCompanyId]
  );

  const renameScenario = React.useCallback(
    async (values, targetScenario = selectedScenario) => {
      if (!targetScenario) return;
      try {
        const response = await postRightworkAPI(
          API_update_labor_model_scenario,
          {
            company_id: selectedCompanyId,
            scenario_uuid: targetScenario.uuid,
            expected_revision: targetScenario.revision,
            ...values,
          }
        );
        updateScenarioState(response?.data?.redux?.payload?.scenario);
        toast.success("Sandbox scenario renamed", { autoClose: 2500 });
      } catch (error) {
        toast.error(
          error.response?.data?.system?.message ||
            "Unable to update sandbox scenario",
          { autoClose: 4000 }
        );
        throw error;
      }
    },
    [selectedCompanyId, selectedScenario, updateScenarioState]
  );

  const archiveScenario = React.useCallback(async (targetScenario = selectedScenario) => {
    if (!targetScenario) return;
    try {
      await postRightworkAPI(API_archive_labor_model_scenario, {
        company_id: selectedCompanyId,
        scenario_uuid: targetScenario.uuid,
        expected_revision: targetScenario.revision,
      });
      setSelectedScenario(null);
      setSelectedScenarioUuid(null);
      const remainingScenarios = await refreshScenarioList();
      if (!remainingScenarios.length) {
        setSelectedPublication(null);
        setSelectedPublicationUuid(activePublication?.uuid || null);
        setModelView("published");
      }
      toast.success("Sandbox scenario deleted", { autoClose: 2500 });
    } catch (error) {
      toast.error(
        error.response?.data?.system?.message ||
          "Unable to delete sandbox scenario",
        { autoClose: 4000 }
      );
      throw error;
    }
  }, [
    activePublication,
    refreshScenarioList,
    selectedCompanyId,
    selectedScenario,
  ]);

  const compareScenario = React.useCallback(
    (scenario) => {
      if (!scenario?.uuid) return;
      setModelView("saved");
      if (selectedScenario?.uuid === scenario.uuid) {
        setScenarioCompareOpen(true);
        return;
      }
      setPendingCompareScenarioUuid(scenario.uuid);
      setSelectedScenario(null);
      setSelectedScenarioUuid(scenario.uuid);
    },
    [selectedScenario]
  );

  const loadScenarioComparison = React.useCallback(
    async (comparisonType, comparisonUuid) => {
      const response = await getRightworkAPI(
        API_compare_labor_model_scenario,
        {
          company_id: selectedCompanyId,
          scenario_uuid: selectedScenario.uuid,
          comparison_type: comparisonType,
          comparison_uuid: comparisonUuid,
        }
      );
      return response?.data?.redux?.payload || {};
    },
    [selectedCompanyId, selectedScenario]
  );

  React.useEffect(() => {
    if (readOnlyModel || !selectedScenario?.uuid) {
      setScenarioChangeCount(null);
      setScenarioChangeCountScenarioUuid(null);
      setScenarioChangeCountLoading(false);
      return undefined;
    }
    let current = true;
    const requestScenarioUuid = selectedScenario.uuid;
    setScenarioChangeCount(null);
    setScenarioChangeCountScenarioUuid(requestScenarioUuid);
    setScenarioChangeCountLoading(true);
    loadScenarioComparison("active", null)
      .then((result) => {
        if (!current) return;
        const count = Number(result?.change_count);
        const nextCount = Number.isFinite(count) ? count : null;
        setScenarioChangeCount(nextCount);
        if (nextCount !== null) {
          setData((currentData) => {
            const scenarioResponse = currentData?.laborModelScenarios;
            const payload = scenarioResponse?.redux?.payload;
            if (!payload) return currentData;
            return {
              ...currentData,
              laborModelScenarios: {
                ...scenarioResponse,
                redux: {
                  ...scenarioResponse.redux,
                  payload: {
                    ...payload,
                    scenarios: (payload.scenarios || []).map((item) =>
                      item.uuid === requestScenarioUuid
                        ? { ...item, change_count: nextCount }
                        : item
                    ),
                  },
                },
              },
            };
          });
        }
      })
      .catch(() => {
        if (current) setScenarioChangeCount(null);
      })
      .finally(() => {
        if (current) setScenarioChangeCountLoading(false);
      });
    return () => {
      current = false;
    };
  }, [
    activePublication?.uuid,
    loadScenarioComparison,
    readOnlyModel,
    selectedScenario?.revision,
    selectedScenario?.uuid,
  ]);

  const viewPublication = React.useCallback(
    (publication) => {
      setPublicationHistoryOpen(false);
      if (
        modelView === "published" &&
        selectedPublication?.uuid === publication.uuid
      ) {
        return;
      }
      setSelectedPublication(null);
      setSelectedPublicationUuid(publication.uuid);
      setModelView("published");
    },
    [modelView, selectedPublication]
  );

  const postScenarioLaborStandard = async (
    laborStandardUuid,
    type,
    name,
    departmentId,
    companyTimeWindowUuid,
    companyTimeWindowName,
    dynamicStartTime,
    dynamicEndTime,
    fixedStartOffsetQhnum,
    fixedEndOffsetQhnum,
    weekDays,
    roleIds,
    constantTimeMinutes,
    scalableTimeMinutes,
    laborDriverUuid,
    laborProgramFlagCompanyUuid,
    staffingPeopleCount,
    isMinimumStaffing,
    isMaximumStaffing = false
  ) => {
    const department = (data.roleTypes || []).find(
      (item) => Number(item.id) === Number(departmentId)
    );
    const snapshot = upsertScenarioStandard(selectedScenario.snapshot, {
      uuid: laborStandardUuid,
      type,
      name,
      department_id: departmentId,
      department_name: department?.role_type,
      company_time_window_uuid: companyTimeWindowUuid,
      company_time_window_name: companyTimeWindowName,
      dynamic_start_time: dynamicStartTime,
      dynamic_end_time: dynamicEndTime,
      fixed_start_offset_qhnum: fixedStartOffsetQhnum,
      fixed_end_offset_qhnum: fixedEndOffsetQhnum,
      week_days: JSON.parse(weekDays),
      role_ids: JSON.parse(roleIds || "[]"),
      constant_time_minutes: constantTimeMinutes,
      scalable_time_minutes: scalableTimeMinutes,
      labor_driver_uuid: laborDriverUuid,
      program_flag_uuid: laborProgramFlagCompanyUuid,
      staffing_people_count: staffingPeopleCount,
      is_minimum_staffing: isMinimumStaffing,
      is_maximum_staffing: isMaximumStaffing,
    });
    return saveScenarioSnapshot(snapshot, "Labor standard saved");
  };

  const deleteScenarioLaborStandard = async (uuid) =>
    saveScenarioSnapshot(
      deleteScenarioStandard(selectedScenario.snapshot, uuid),
      "Labor standard removed"
    );

  const editScenarioTimeWindow = async (values) => {
    const result = upsertScenarioTimeWindow(selectedScenario.snapshot, {
      uuid: values.company_time_window_uuid,
      ...values,
    });
    await saveScenarioSnapshot(result.snapshot, "Time window saved");
    return result.timeWindow;
  };

  const deleteScenarioTimeWindowHandler = async (uuid) =>
    saveScenarioSnapshot(
      deleteScenarioTimeWindow(selectedScenario.snapshot, uuid),
      "Time window removed"
    );

  const postScenarioAttributeDriver = async (uuid, name, value) =>
    saveScenarioSnapshot(
      upsertScenarioAttributeDriver(selectedScenario.snapshot, {
        uuid,
        name,
        value,
      }),
      "Attribute driver saved"
    );

  const postScenarioSalesDriver = async (values) => {
    await saveScenarioSnapshot(
      upsertScenarioSalesDriver(selectedScenario.snapshot, values),
      "Sales driver saved"
    );
    return {};
  };

  const postScenarioRoutedDriver = async (values) => {
    await saveScenarioSnapshot(
      upsertScenarioRoutedDriver(selectedScenario.snapshot, values),
      "Routed sales driver saved"
    );
    return {};
  };

  const deleteScenarioDriverHandler = async (uuid) =>
    saveScenarioSnapshot(
      deleteScenarioDriver(selectedScenario.snapshot, uuid),
      "Labor driver removed"
    );

  const postScenarioProgramFlag = async (uuid, defaultValue, name) =>
    saveScenarioSnapshot(
      upsertScenarioProgramFlag(selectedScenario.snapshot, {
        uuid,
        defaultValue,
        name,
      }),
      "Program flag saved"
    );

  const deleteScenarioProgramFlagHandler = async (uuid) =>
    saveScenarioSnapshot(
      deleteScenarioProgramFlag(selectedScenario.snapshot, uuid),
      "Program flag removed"
    );

  const postScenarioStoreAttribute = async (driverUuid, value, storeId) =>
    saveScenarioSnapshot(
      setScenarioAttributeStoreValue(
        selectedScenario.snapshot,
        driverUuid,
        storeId,
        value
      ),
      "Store attribute saved"
    );

  const postScenarioStoreFlag = async (value, storeId, flagUuid) =>
    saveScenarioSnapshot(
      setScenarioProgramFlagStoreValue(
        selectedScenario.snapshot,
        flagUuid,
        storeId,
        value
      ),
      "Store flag saved"
    );

  const postScenarioLocationAdjustment = async (storeId, values) =>
    saveScenarioSnapshot(
      setScenarioLocationAdjustment(
        selectedScenario.snapshot,
        storeId,
        values
      ),
      "Store adjustment saved"
    );

  // ----------- POST APIs ----------- //

  const post_labor_standard = async (
    labor_standard_uuid,
    type,
    name,
    department_id,
    company_time_window_uuid,
    company_time_window_name,
    dynamic_start_time,
    dynamic_end_time,
    fixed_start_offset_qhnum,
    fixed_end_offset_qhnum,
    week_days,
    role_ids,
    constant_time_minutes,
    scalable_time_minutes,
    labor_driver_uuid,
    labor_program_flag_company_uuid,
    staffing_people_count,
    is_minimum_staffing,
    is_maximum_staffing = false
  ) => {
    // Prepare data to send
    const requestData = {
      labor_standard_uuid: labor_standard_uuid,
      company_id: selectedCompanyId,
      type: type,
      name: name,
      department_id: department_id,
      company_time_window_uuid: company_time_window_uuid,
      company_time_window_name: company_time_window_name,
      dynamic_start_time: dynamic_start_time,
      dynamic_end_time: dynamic_end_time,
      fixed_start_offset_qhnum: fixed_start_offset_qhnum,
      fixed_end_offset_qhnum: fixed_end_offset_qhnum,
      week_days: week_days,
      role_ids: role_ids,
      constant_time_minutes: constant_time_minutes,
      scalable_time_minutes: scalable_time_minutes,
      labor_driver_uuid: labor_driver_uuid,
      labor_program_flag_company_uuid: labor_program_flag_company_uuid,
      staffing_people_count: staffing_people_count,
      is_minimum_staffing: is_minimum_staffing,
      is_maximum_staffing: is_maximum_staffing,
    };

    // console.log("Company ID", requestData);

    try {
      toast.loading("Updating Labor Standard...", { autoClose: false });

      const res = await postRightworkAPI(API_post_labor_standard, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Labor Standard Updated!", { autoClose: 3000 });

        fetch_data(
          {
            laborStandards: API_get_labor_standards,
            companyTimeWindows: API_get_company_time_windows,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
        // console.log("ERRORDATA", error.response?.data?.system?.data);
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const delete_labor_standard = async (labor_standard_uuid) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      labor_standard_uuid: labor_standard_uuid,
    };

    try {
      toast.loading("Deleting Labor Standared...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_labor_standard,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Labor Standard Deleted!", { autoClose: 3000 });

        fetch_data(
          {
            laborStandards: API_get_labor_standards,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const post_labor_driver_company_attribute = async (uuid, name, value) => {
    // Prepare data to send
    const requestData = {
      labor_driver_attribute_company_uuid: uuid,
      company_id: selectedCompanyId,
      name: name,
      value: value,
    };

    // console.log("Company ID", requestData);

    try {
      toast.loading("Updating Attribute Labor Driver...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_labor_driver_company_attribute,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Attribute Labor Driver Updated!", { autoClose: 3000 });

        fetch_data(
          {
            laborDrivers: API_get_labor_drivers,
            laborDriverStoreAttributes: API_get_labor_driver_store_attributes,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const post_labor_driver_sales = async ({
    labor_driver_sales_uuid,
    name,
    filter_column,
    filter_values,
    target_column,
    aggregate_type,
  }) => {
    const requestData = {
      labor_driver_sales_uuid,
      company_id: selectedCompanyId,
      name,
      filter_column,
      filter_values,
      target_column,
      aggregate_type,
    };

    try {
      toast.loading("Saving Sales Labor Driver...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_labor_driver_sales,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Sales Labor Driver Saved!", { autoClose: 3000 });

        fetch_data(
          {
            laborDrivers: API_get_labor_drivers,
          },
          api_params
        );
        return res?.data?.redux?.payload;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
      throw error;
    }
  };

  const backfill_labor_driver_sales = async (laborDriverUuids) => {
    const requestData = {
      company_id: selectedCompanyId,
    };
    if (Array.isArray(laborDriverUuids) && laborDriverUuids.length) {
      requestData.labor_driver_uuids = laborDriverUuids;
    }

    try {
      const res = await postRightworkAPI(
        API_post_backfill_labor_driver_sales,
        requestData
      );
      return res?.data?.redux?.payload;
    } catch (error) {
      if (error.response?.data?.system?.message) {
        throw new Error(error.response.data.system.message);
      }
      throw error;
    }
  };

  const post_labor_driver_routed_sales = async ({
    labor_driver_routed_sales_uuid,
    name,
    target_store_id,
    source_metric_name,
    source_store_ids,
    include_target_store_sales,
    source_date_offset_days,
    is_active,
  }) => {
    const requestData = {
      labor_driver_routed_sales_uuid,
      company_id: selectedCompanyId,
      name,
      target_store_id,
      source_metric_name,
      source_store_ids,
      include_target_store_sales,
      source_date_offset_days,
      is_active,
    };

    try {
      toast.loading("Saving Routed Sales Group...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_labor_driver_routed_sales,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Routed Sales Group Saved!", { autoClose: 3000 });

        fetch_data(
          {
            laborDrivers: API_get_labor_drivers,
          },
          api_params
        );
        return res?.data?.redux?.payload;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
      throw error;
    }
  };

  const backfill_labor_driver_routed_sales = async (laborDriverUuids) => {
    const requestData = {
      company_id: selectedCompanyId,
    };
    if (Array.isArray(laborDriverUuids) && laborDriverUuids.length) {
      requestData.labor_driver_uuids = laborDriverUuids;
    }

    try {
      const res = await postRightworkAPI(
        API_post_backfill_labor_driver_routed_sales,
        requestData
      );
      return res?.data?.redux?.payload;
    } catch (error) {
      if (error.response?.data?.system?.message) {
        throw new Error(error.response.data.system.message);
      }
      throw error;
    }
  };

  const delete_labor_driver = async (labor_driver_uuid) => {
    const requestData = {
      company_id: selectedCompanyId,
      labor_driver_uuid,
    };

    try {
      toast.loading("Deleting Labor Driver...", { autoClose: false });

      const res = await postRightworkAPI(API_delete_labor_driver, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Labor Driver Deleted!", { autoClose: 3000 });

        fetch_data(
          {
            laborDrivers: API_get_labor_drivers,
            laborDriverStoreAttributes: API_get_labor_driver_store_attributes,
          },
          api_params
        );
        return res?.data?.redux?.payload;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
      throw error;
    }
  };

  //   * company_id
  // * name
  // * value
  // * store_id

  const post_labor_driver_store_attribute = async (
    labor_driver_attribute_company_uuid,
    value,
    store_id
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      labor_driver_attribute_company_uuid: labor_driver_attribute_company_uuid,
      value: value,
      store_id: store_id,
    };

    try {
      toast.loading("Updating Store Attribute Value...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_labor_driver_store_attribute,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Store Attribute Value Updated!", { autoClose: 3000 });

        fetch_data(
          {
            laborDriverStoreAttributes: API_get_labor_driver_store_attributes,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const edit_time_window = async (timeWindowData) => {
    const requestData = {
      company_time_window_uuid: timeWindowData.company_time_window_uuid,
      company_id: selectedCompanyId,
      name: timeWindowData.name,
      dynamic_start_time: timeWindowData.dynamic_start_time,
      dynamic_end_time: timeWindowData.dynamic_end_time,
      fixed_start_offset_qhnum: timeWindowData.fixed_start_offset_qhnum,
      fixed_end_offset_qhnum: timeWindowData.fixed_end_offset_qhnum,
    };

    try {
      toast.loading("Updating Time Window...", { autoClose: false });

      const res = await postRightworkAPI(
        API_edit_company_time_window,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Time Window Updated!", { autoClose: 3000 });

        fetch_data(
          {
            companyTimeWindows: API_get_company_time_windows,
          },
          api_params
        );

        return res.data?.redux?.payload?.company_time_window;
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed

      return null;
    }
  };

  const delete_time_window = async (company_time_window_uuid) => {
    // Prepare data to send
    const requestData = {
      company_time_window_uuid: company_time_window_uuid,
      company_id: selectedCompanyId,
    };

    try {
      toast.loading("Deleting Time Window...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_company_time_windows,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Time Window Deleted!", { autoClose: 3000 });

        fetch_data(
          {
            companyTimeWindows: API_get_company_time_windows,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const post_labor_program_flag_company = async (
    labor_program_flag_company_uuid,
    value,
    name
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      labor_program_flag_company_uuid: labor_program_flag_company_uuid,
      default: value,
      name: name,
    };

    try {
      toast.loading("Updating Flag...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_labor_program_flag_company,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Flag Updated!", { autoClose: 3000 });

        fetch_data(
          {
            laborProgramFlags: API_get_labor_program_flags,
            laborProgramFlagsStoreOverrides:
              API_get_labor_program_flags_with_store_overrides,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const delete_labor_program_flag_company = async (
    labor_program_flag_company_uuid
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      labor_program_flag_company_uuid: labor_program_flag_company_uuid,
    };

    try {
      toast.loading("Deleting Flag...", { autoClose: false });

      const res = await postRightworkAPI(
        API_delete_labor_program_flag_company,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Flag Deleted!", { autoClose: 3000 });

        fetch_data(
          {
            laborProgramFlags: API_get_labor_program_flags,
            laborProgramFlagsStoreOverrides:
              API_get_labor_program_flags_with_store_overrides,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const post_labor_program_flag_store = async (
    override,
    store_id,
    labor_program_flag_company_uuid
  ) => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
      override: override,
      store_id: store_id,
      labor_program_flag_company_uuid: labor_program_flag_company_uuid,
    };

    try {
      toast.loading("Updating Store Flag Value...", { autoClose: false });

      const res = await postRightworkAPI(
        API_post_labor_program_flag_store,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Store Flag Value Updated!", { autoClose: 3000 });

        fetch_data(
          {
            laborProgramFlagsStoreOverrides:
              API_get_labor_program_flags_with_store_overrides,
            laborProgramFlags: API_get_labor_program_flags,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  // [POST] api/labor_model_info/publish_labor_standard
  // * company_id

  const publish_labor_standard = async (options = {}) => {
    const requestCompanyId = selectedCompanyId;
    const requestId = defaultDataRequestRef.current + 1;
    defaultDataRequestRef.current = requestId;
    const shouldApplyPublishRefresh = () =>
      defaultDataRequestRef.current === requestId &&
      selectedCompanyIdRef.current === requestCompanyId;
    const requestApiParams = {
      ...api_params,
      company_id: requestCompanyId,
    };
    // Prepare data to send
    const requestData = {
      company_id: requestCompanyId,
      name: options.name,
      effective_date: options.effectiveDate,
      idempotency_key: options.idempotencyKey,
      scenario_uuid: selectedScenario?.uuid,
      expected_revision: selectedScenario?.revision,
      comment: options.comment || "",
    };

    try {
      toast.loading("Publishing and Recalculating Labor...", {
        autoClose: false,
      });

      const res = await postRightworkAPI(
        API_publish_labor_standard,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        const recalculation = res.data?.redux?.payload?.recalculation || {};
        const forecastCount =
          recalculation.forecast_store_dates_recalculated || 0;
        const earnedCount =
          recalculation.earned_store_dates_recalculated || 0;
        toast.success(
          `Labor model published! Updated ${forecastCount} forecast and ${earnedCount} earned store-days.`,
          { autoClose: 5000 }
        );

        if (!shouldApplyPublishRefresh()) {
          return true;
        }

        const publishedScenario = res.data?.redux?.payload?.scenario;
        if (publishedScenario) {
          updateScenarioState(publishedScenario);
        }

        await fetch_data(
          {
            laborStandards: API_get_labor_standards,
            companyTimeWindows: API_get_company_time_windows,
            laborModelPublications: API_get_labor_model_publications,
            laborModelScenarios: API_get_labor_model_scenarios,
          },
          requestApiParams,
          false,
          {},
          shouldApplyPublishRefresh
        );
        return true;
      }
      throw new Error(`Unexpected publish response: ${res.status}`);
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
      throw error;
    }
  };

  const restore_labor_model_publication = async (publication) => {
    try {
      toast.loading("Creating sandbox scenario from published model...", {
        autoClose: false,
      });
      const existingNames = new Set(
        laborModelScenarios.map((item) => getScenarioDisplayName(item))
      );
      const scenarioName = getRestoredScenarioName(publication, existingNames);
      const res = await postRightworkAPI(API_create_labor_model_scenario, {
        company_id: selectedCompanyId,
        name: scenarioName,
        description: `Copied from ${getPublicationDisplayName(publication)}.`,
        source_type: "publication",
        source_uuid: publication.uuid,
      });
      const scenario = res.data?.redux?.payload?.scenario;
      await refreshScenarioList(scenario.uuid);
      toast.dismiss();
      toast.success(
        `Created ${getScenarioDisplayName(scenario)}. Nothing was published.`,
        { autoClose: 4000 }
      );
      return { scenario };
    } catch (error) {
      toast.dismiss();
      const message =
        error.response?.data?.system?.message ||
        "Failed to create the sandbox scenario.";
      toast.error(message, { autoClose: 5000 });
      throw error;
    }
  };

  // [POST] api/labor_model_info/revert_labor_standard
  // * company_id

  const revert_labor_standard = async () => {
    // Prepare data to send
    const requestData = {
      company_id: selectedCompanyId,
    };

    try {
      toast.loading("Resetting Sandbox...", { autoClose: false });

      const res = await postRightworkAPI(
        API_revert_labor_standard,
        requestData
      );

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Sandbox Reset!", { autoClose: 3000 });

        fetch_data(
          {
            laborStandards: API_get_labor_standards,
            companyTimeWindows: API_get_company_time_windows,
          },
          api_params
        );
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error); // Handle error as needed
    }
  };

  const renderModelSections = () => {
    if (
      (readOnlyModel ? publicationLoading : scenarioLoading) ||
      !modelEditorData
    ) {
      return (
        <div className="flex min-h-[360px] items-center justify-center">
          <LoadSpinner />
        </div>
      );
    }

    const attributeCount = modelEditorData.laborDrivers.filter(
      (driver) => driver.labor_driver_attribute !== null
    ).length;
    const salesCount = Math.max(
      modelEditorData.laborDrivers.length - attributeCount,
      0
    );
    const flagCount = modelEditorData.laborProgramFlags.length;
    const customizedStoreCount = modelEditorData.storesWithAdjustments.filter(
      (store) =>
        Math.abs(Number(store.labor_model_fit_factor ?? 1) - 1) > 0.0001 ||
        (store.labor_budget_mode && store.labor_budget_mode !== "bottom_up")
    ).length;
    const setupSummary = {
      salesDrivers: salesCount,
      attributes: attributeCount,
      flags: flagCount,
      totalDrivers: modelEditorData.laborDrivers.length + flagCount,
      standards: modelEditorData.laborStandards.length,
      adjustments: customizedStoreCount,
      stores: modelEditorData.storesWithAdjustments.length,
    };
    const workspaceModelName = readOnlyModel
      ? getPublicationDisplayName(selectedPublication)
      : getScenarioDisplayName(selectedScenario);
    const workspaceModelDescription = readOnlyModel
      ? String(selectedPublication?.publish_comment || "").trim()
      : getScenarioDisplayDescription(selectedScenario);
    const workspaceImpactDeltas = guidedEvaluationFresh
      ? getLaborModelImpactDeltas(
          guidedEvaluation,
          laborPlanSummary,
          companyStores.map((store) => String(store.id))
        )
      : null;
    const displayedScenarioChangeCount =
      scenarioChangeCountScenarioUuid === selectedScenario?.uuid
        ? scenarioChangeCount
        : null;
    const displayedScenarioChangeCountLoading = Boolean(
      !readOnlyModel &&
        selectedScenario?.uuid &&
        (scenarioChangeCountScenarioUuid !== selectedScenario.uuid ||
          scenarioChangeCountLoading)
    );

    const manualSections = (
      <div>
        <LaborModelSection
            stepNumber={1}
            title="Labor Drivers"
            description="Define the sales metrics, attributes, and flags that labor standards use."
            summary={`${salesCount} sales · ${attributeCount} attributes · ${flagCount} flags`}
            grouped
            isLastGroupedItem={false}
          >
            <LaborDriversDisplay
              companyId={selectedCompanyId}
              selectedCompany={companyOptions.find(
                (companyOption) => companyOption.id === selectedCompanyId
              )}
              laborDrivers={modelEditorData.laborDrivers}
              post_labor_driver_company_attribute={postScenarioAttributeDriver}
              post_labor_driver_sales={postScenarioSalesDriver}
              post_labor_driver_routed_sales={postScenarioRoutedDriver}
              delete_labor_driver={deleteScenarioDriverHandler}
              storesInfo={data.storesInfo?.redux?.payload?.stores}
              laborProgramFlags={modelEditorData.laborProgramFlags}
              post_labor_program_flag_company={postScenarioProgramFlag}
              delete_labor_program_flag_company={
                deleteScenarioProgramFlagHandler
              }
              laborDriverStoreAttributes={
                modelEditorData.laborDriverStoreAttributes
              }
              post_labor_driver_store_attribute={postScenarioStoreAttribute}
              laborProgramFlagsStoreOverrides={
                modelEditorData.laborProgramFlagsStoreOverrides
              }
              post_labor_program_flag_store={postScenarioStoreFlag}
              scenarioMode
              readOnly={readOnlyModel}
            />
        </LaborModelSection>

        <LaborModelSection
            stepNumber={2}
            title="Labor Standards"
            description="Build the rules that convert labor drivers into earned and forecasted labor."
            summary={`${modelEditorData.laborStandards.length} standards`}
            allowOverflow
            grouped
            isLastGroupedItem={false}
          >
            <LaborModelInnerDisplay
              laborStandards={modelEditorData.laborStandards}
              laborStandardsSandbox={modelEditorData.laborStandards}
              post_labor_standard={postScenarioLaborStandard}
              delete_labor_standard={deleteScenarioLaborStandard}
              laborDrivers={modelEditorData.laborDrivers}
              companyTimeWindows={modelEditorData.companyTimeWindows}
              edit_time_window={editScenarioTimeWindow}
              delete_time_window={deleteScenarioTimeWindowHandler}
              roleTypes={data?.roleTypes}
              roleNames={data.roleNames}
              company_id={selectedCompanyId}
              storeNames={companyStores}
              homeStore={selectedStoreId}
              businessWeekStartDayIndex={getBusinessWeekStartDayIndex(
                props.storeNames,
                selectedStoreId
              )}
              laborProgramFlags={modelEditorData.laborProgramFlags}
              scenarioMode
              readOnly={readOnlyModel}
              embedded
              onOpenOptimizationDrawer={
                readOnlyModel ? null : () => setLaborStandardsFitOpen(true)
              }
            />
        </LaborModelSection>

        <LaborModelSection
            stepNumber={3}
            title="Store Adjustments"
            description="Calibrate by store with multipliers or fixed targets."
            summary={`${customizedStoreCount} customized · ${modelEditorData.storesWithAdjustments.length} locations`}
            grouped
            isLastGroupedItem={true}
          >
            <LaborModelStoreAdjustmentsTable
              stores={modelEditorData.storesWithAdjustments}
              laborPlanSummary={laborPlanSummary}
              onLoadMetricPreview={loadLaborPlanSummary}
              onSave={postScenarioLocationAdjustment}
              readOnly={readOnlyModel}
              embedded
            />
        </LaborModelSection>

        <div
            id="labor-model-impact-review"
            className="border-t border-slate-200 scroll-mt-6"
          >
            <LaborModelPlanSummary
              stepNumber={4}
              target={selectedModelTarget}
              stores={companyStores}
              publications={laborModelPublications}
              evaluations={modelPlanEvaluations}
              evaluationLoadingKey={modelPlanEvaluationLoadingKey}
              evaluationStage={
                modelPlanEvaluationStage?.evaluationKey ===
                modelPlanEvaluationLoadingKey
                  ? modelPlanEvaluationStage.stage
                  : null
              }
              evaluationErrors={modelPlanEvaluationErrors}
              laborPlanSummary={laborPlanSummary}
              laborPlanSummaryError={laborPlanSummaryError}
              activePublicationUuid={activePublication?.uuid}
              onRetryPublishedPlan={(range = {}) =>
                loadLaborPlanSummary(
                  range.weeksBack || 4,
                  getCurrentBusinessWeekRange(
                    companyStores[0]?.business_week_start_day,
                    range.weeksForward || 4
                  )
                )
              }
              onPublishedRangeChange={(range) =>
                loadLaborPlanSummary(
                  range.weeksBack,
                  getCurrentBusinessWeekRange(
                    companyStores[0]?.business_week_start_day,
                    range.weeksForward
                  )
                )
              }
              storeAdjustments={
                modelEditorData?.snapshot?.location_adjustments || []
              }
              onEvaluate={readOnlyModel ? null : runModelPlanEvaluation}
              onAdvancedAnalysis={
                readOnlyModel ? null : () => setScenarioEvaluationOpen(true)
              }
              embeddedReviewPanel
            />
          </div>
      </div>
    );

    return (
      <div className="bg-slate-50 px-6 py-6 sm:px-4">
        <div className="mx-auto w-full max-w-[1120px]">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <LaborModelWorkspaceHeader
              modelName={workspaceModelName}
              modelDescription={workspaceModelDescription}
              readOnly={readOnlyModel}
              connected
              summary={setupSummary}
              evaluationFresh={!readOnlyModel && guidedEvaluationFresh}
              evaluationLoading={
                modelPlanEvaluationLoadingKey === guidedEvaluationKey
              }
              evaluationError={modelPlanEvaluationErrors[guidedEvaluationKey]}
              sandboxMetrics={
                !readOnlyModel && guidedEvaluationFresh
                  ? getSandboxHeaderMetrics(guidedEvaluation, companyStores)
                  : null
              }
              publishedMetrics={
                selectedPublishedPlanAvailable && !laborPlanSummaryError
                  ? getPublishedHeaderMetrics(laborPlanSummary, companyStores)
                  : null
              }
              evaluationDeltas={readOnlyModel ? null : workspaceImpactDeltas}
              changeCount={
                readOnlyModel
                  ? publishedChangeCount
                  : displayedScenarioChangeCount
              }
              changeCountLoading={
                readOnlyModel ? false : displayedScenarioChangeCountLoading
              }
              onOpenAssistant={() => setAssistantOpen(true)}
              onCreateSandbox={
                readOnlyModel && selectedPublication
                  ? async () => {
                      const result = await restore_labor_model_publication(
                        selectedPublication
                      );
                      if (result?.scenario) {
                        updateScenarioState(result.scenario);
                        setModelView("saved");
                      }
                      return result;
                    }
                  : null
              }
              onViewChanges={() => setScenarioCompareOpen(true)}
            />

            {manualSections}
          </div>

          <Modal
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
            keepMounted
            aria-labelledby="labor-model-assistant-title"
          >
            <div className="absolute left-1/2 top-1/2 h-[min(820px,calc(100vh-32px))] w-[min(920px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl bg-gray-50 shadow-2xl outline-none sm:h-[calc(100vh-24px)] sm:w-[calc(100vw-24px)]">
              <LaborModelGuidedSetup
                companyId={selectedCompanyId}
                scenario={readOnlyModel ? null : selectedScenario}
                publication={readOnlyModel ? selectedPublication : null}
                readOnly={readOnlyModel}
                connected
                modal
                onClose={() => setAssistantOpen(false)}
                setupSummary={setupSummary}
                scenarioChangeCount={displayedScenarioChangeCount}
                scenarioChangeCountLoading={displayedScenarioChangeCountLoading}
                evaluation={
                  !readOnlyModel && guidedEvaluationFresh
                    ? guidedEvaluation
                    : null
                }
                evaluationFresh={!readOnlyModel && guidedEvaluationFresh}
                laborPlanSummary={laborPlanSummary}
                onScenarioApplied={updateScenarioState}
              />
            </div>
          </Modal>
        </div>
      </div>
    );
  };

  return (
    <>
      {!isLoading ? (
        <div className="min-h-screen w-full bg-white">
            <TopBarDisplayTitleNoLocationSelect
              title="Labor Model"
              titleExtra={
                <div className="w-64 max-w-[40vw]">
                  {!props.settingsCompanyId && (<CompanyNameSidebar
                    companyOptions={companyOptions}
                    selectedCompanyId={selectedCompanyId}
                    setSelectedCompanyId={handleCompanyChange}
                  />)}
                </div>
              }
            />
            <LaborModelScenarioHeader
              scenarios={laborModelScenarios}
              publications={laborModelPublications}
              selectedScenario={selectedScenario}
              selectedScenarioUuid={selectedScenarioUuid}
              activePublication={activePublication}
              viewMode={modelView}
              selectedPublication={selectedPublication}
              activePublicationUuid={activePublication?.uuid}
              onSelect={(scenarioUuid) => {
                setModelView("saved");
                if (selectedScenario?.uuid === scenarioUuid) {
                  return;
                }
                setSelectedScenario(null);
                setSelectedScenarioUuid(scenarioUuid);
              }}
              onCreate={createScenario}
              onRename={renameScenario}
              onArchive={archiveScenario}
              onCompare={compareScenario}
              onHistory={() => setPublicationHistoryOpen(true)}
              onPublish={() => setOpenPublishWarningModal(true)}
              onViewPublication={viewPublication}
            />
            {renderModelSections()}
            <PublishLaborModelWarningModal
              open={openPublishWarningModal}
              handleClose={() => setOpenPublishWarningModal(false)}
              publish_labor_standard={publish_labor_standard}
              weekStartDay={companyStores[0]?.business_week_start_day || "monday"}
              selectedScenario={selectedScenario}
              evaluation={
                evaluationSummariesByScenario[selectedScenario?.uuid] || null
              }
              onEvaluate={runQuickEvaluation}
              evaluating={quickEvaluationLoading}
            />
            <LaborModelPublicationHistoryModal
              open={publicationHistoryOpen}
              onClose={() => setPublicationHistoryOpen(false)}
              publications={laborModelPublications}
              onRestore={restore_labor_model_publication}
              onView={viewPublication}
              activePublicationUuid={activePublication?.uuid}
            />
            <LaborModelScenarioCompareModal
              open={scenarioCompareOpen}
              onClose={() => setScenarioCompareOpen(false)}
              scenario={selectedScenario}
              publication={readOnlyModel ? selectedPublication : null}
              scenarios={laborModelScenarios}
              activePublication={activePublication}
              onLoad={readOnlyModel ? null : loadScenarioComparison}
              comparisonResult={
                readOnlyModel
                  ? {
                      changes:
                        selectedPublicationMetadata?.changes_from_live || [],
                      change_count: publishedChangeCount || 0,
                    }
                  : null
              }
            />
            <LaborModelTestModal
              open={scenarioEvaluationOpen}
              handleClose={() => setScenarioEvaluationOpen(false)}
              company_id={selectedCompanyId}
              storeNames={companyStores}
              homeStore={selectedStoreId}
              roleTypes={data?.roleTypes}
              scenarioUuid={selectedScenario?.uuid}
              scenarioRevision={selectedScenario?.revision}
              scenarioName={getScenarioDisplayName(selectedScenario)}
              onEvaluationComplete={(evaluationSummary) => {
                if (!evaluationSummary.scenarioUuid) return;
                setEvaluationSummariesByScenario((current) => ({
                  ...current,
                  [evaluationSummary.scenarioUuid]: evaluationSummary,
                }));
              }}
            />
            <LaborStandardsOptimizationDrawer
              open={laborStandardsFitOpen}
              onClose={() => setLaborStandardsFitOpen(false)}
              companyId={selectedCompanyId}
              companyName={
                companyOptions.find(
                  (company) => company.id === selectedCompanyId
                )?.name
              }
              stores={companyStores}
              scenarioUuid={selectedScenario?.uuid}
              scenarioRevision={selectedScenario?.revision}
              standardCount={modelEditorData?.laborStandards?.length}
              onApplied={(scenario) => {
                if (scenario) {
                  updateScenarioState(scenario);
                }
                setLaborStandardsFitOpen(false);
              }}
            />
        </div>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      )}
    </>
  );
}

export default LaborModelOuterDisplay;
